import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Edit, Delete, VpnKey, Visibility, Search, Add } from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';
import {
  GlassCard,
  SectionTitle,
  TableWrapper,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  ActionIconButton,
} from './AdminStyles';
import toast from 'react-hot-toast';

// Interfaces
interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  date_joined?: string;
}

interface Conversation {
  id: number;
  user_id: number;
  title: string;
  created_at: string;
}

const UserManagement: React.FC = () => {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Dialog States
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openConversationsDialog, setOpenConversationsDialog] = useState(false);

  // Selected Data State
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserConversations, setSelectedUserConversations] = useState<Conversation[]>([]);

  // Form Data States
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    is_staff: false,
  });
  const [newPassword, setNewPassword] = useState('');

  // --- Fetching Data ---
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getUsers({ search: searchTerm });
      setUsers(data);
    } catch (err) {
      toast.error('Napaka pri nalaganju uporabnikov.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchConversations = async (userId: number) => {
    try {
      const data = await adminApi.getUserConversations(userId);
      setSelectedUserConversations(data);
    } catch (err) {
      toast.error('Napaka pri nalaganju pogovorov.');
    }
  };

  // --- Handlers ---
  const handleSearchSubmit = () => fetchUsers();

  // Add User Logic
  const handleOpenAdd = () => {
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      is_staff: false,
    });
    setOpenAddDialog(true);
  };

  const handleAddUser = async () => {
    try {
      await adminApi.createUser(formData);
      toast.success('Uporabnik uspešno ustvarjen!');
      setOpenAddDialog(false);
      fetchUsers();
    } catch (err: any) {
      console.error(err);
      toast.error('Napaka pri ustvarjanju: ' + (err.response?.data?.error || 'Neznana napaka'));
    }
  };

  // Edit User Logic
  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      is_staff: user.is_staff,
    });
    setOpenEditDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await adminApi.updateUser(selectedUser.id, formData);
      toast.success('Uporabnik posodobljen!');
      setOpenEditDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error('Napaka pri posodabljanju.');
    }
  };

  // Delete User Logic
  const handleOpenDelete = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await adminApi.deleteUser(selectedUser.id);
      toast.success('Uporabnik izbrisan.');
      setOpenDeleteDialog(false);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Napaka pri brisanju.');
    }
  };

  // Reset Password Logic
  const handleOpenReset = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setOpenResetDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      await adminApi.resetUserPassword(selectedUser.id, { password: newPassword });
      toast.success('Geslo uspešno spremenjeno.');
      setOpenResetDialog(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Napaka pri menjavi gesla.');
    }
  };

  // View Conversations Logic
  const handleViewConversations = (user: User) => {
    setSelectedUser(user);
    setOpenConversationsDialog(true);
    fetchConversations(user.id);
  };

  return (
    <Box>
      <GlassCard elevation={0}>
        {/* Header Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <SectionTitle>Upravljanje Uporabnikov</SectionTitle>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              placeholder="Išči uporabnike..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'var(--bg-app)',
                  color: 'var(--text-primary)',
                  '& fieldset': { borderColor: 'var(--border-color)' },
                  '&:hover fieldset': { borderColor: 'var(--primary-color)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
                  '& .MuiSvgIcon-root': { color: 'var(--text-secondary)' },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: 'var(--text-secondary)',
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleOpenAdd}
              startIcon={<Add />}
              sx={{
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              }}
            >
              Nov Uporabnik
            </Button>
          </Box>
        </Box>

        {/* Users Table */}
        <TableWrapper>
          <StyledTableContainer>
            <Table sx={{ minWidth: 650 }}>
              <StyledTableHead>
                <StyledTableRow>
                  <TableCell>Uporabnik</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Ime in Priimek</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Akcije</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {users.map((user) => (
                  <StyledTableRow key={user.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.first_name} {user.last_name}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: user.is_staff ? '#e0e7ff' : '#f3f4f6',
                          color: user.is_staff ? '#4338ca' : '#4b5563',
                        }}
                      >
                        {user.is_staff ? 'Admin' : 'Uporabnik'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <ActionIconButton
                        onClick={() => handleViewConversations(user)}
                        title="Pogovori"
                      >
                        <Visibility fontSize="small" />
                      </ActionIconButton>
                      <ActionIconButton onClick={() => handleOpenEdit(user)} title="Uredi">
                        <Edit fontSize="small" />
                      </ActionIconButton>
                      <ActionIconButton
                        onClick={() => handleOpenReset(user)}
                        title="Ponastavi geslo"
                      >
                        <VpnKey fontSize="small" />
                      </ActionIconButton>
                      <ActionIconButton
                        onClick={() => handleOpenDelete(user)}
                        title="Izbriši"
                        style={{ color: '#ef4444', background: '#fee2e2' }}
                      >
                        <Delete fontSize="small" />
                      </ActionIconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </TableWrapper>
      </GlassCard>

      {/* --- DIALOGS --- */}

      {/* Add User Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nov Uporabnik</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Uporabniško ime"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              label="Geslo"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ime"
                fullWidth
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              <TextField
                label="Priimek"
                fullWidth
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_staff}
                  onChange={(e) => setFormData({ ...formData, is_staff: e.target.checked })}
                />
              }
              label="Je Administrator (Staff)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Prekliči</Button>
          <Button variant="contained" onClick={handleAddUser}>
            Ustvari
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Uredi Uporabnika: {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ime"
                fullWidth
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              <TextField
                label="Priimek"
                fullWidth
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_staff}
                  onChange={(e) => setFormData({ ...formData, is_staff: e.target.checked })}
                />
              }
              label="Je Administrator (Staff)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Prekliči</Button>
          <Button variant="contained" onClick={handleUpdateUser}>
            Shrani
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>Ponastavi Geslo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vnesite novo geslo za uporabnika {selectedUser?.username}.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Novo Geslo"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)}>Prekliči</Button>
          <Button onClick={handleResetPassword} color="warning" variant="contained">
            Spremeni Geslo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Izbriši Uporabnika?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ali ste prepričani, da želite izbrisati uporabnika <b>{selectedUser?.username}</b>? Tega
            dejanja ni mogoče razveljaviti.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Prekliči</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Izbriši
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversations Dialog */}
      <Dialog
        open={openConversationsDialog}
        onClose={() => setOpenConversationsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Pogovori: {selectedUser?.username}</DialogTitle>
        <DialogContent dividers>
          {selectedUserConversations.length === 0 ? (
            <Typography align="center" color="textSecondary">
              Uporabnik nima shranjenih pogovorov.
            </Typography>
          ) : (
            <List>
              {selectedUserConversations.map((chat) => (
                <ListItem key={chat.id} divider>
                  <ListItemText
                    primary={chat.title || `Pogovor #${chat.id}`}
                    secondary={new Date(chat.created_at).toLocaleString('sl-SI')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConversationsDialog(false)}>Zapri</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
